import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import { RouteFactory } from "@app/routePaths";
import chevronLeft from "@assets/icon-chevron-left-white.svg";
import chevronRight from "@assets/icon-chevron-right-white.svg";
import { REMOVE_FOCUS_CSS } from "@components/Button/constants";
import {
  Options,
  Splide,
  SplideSlide,
  SplideTrack,
} from "@greglaisph/react-splide";
import PulseEndingScreen from "@pages/Pulse/components/PulseEndingScreen";
import useSendPulseGaData from "@pages/Pulse/hooks/useSendPulseGaData";
import type { PulsePageProps } from "@pages/Pulse/utils/types";
import { cn } from "@util/helpers";

import PulseIndividualMobileArticle from "./PulseIndividualMobileArticle";

export default function PulseMobileCarousel({
  sortedPulseData,
  currentSlide,
  setCurrentSlide,
  setCurrentSlideId,
  currentSlideId,
  backToBt = "/",
}: PulsePageProps): React.ReactElement {
  const navigate = useNavigate();

  const memorisedArticles = useMemo(() => sortedPulseData, [sortedPulseData]);

  const totalLength = memorisedArticles.length + 1;

  const { sendTrackingData, sendCustomEvent } = useSendPulseGaData();

  const options: Options = {
    perMove: 1,
    perPage: 1,
    arrows: false,
    pagination: false,
    speed: 700,
  };

  const optionsForImage: Options = {
    perMove: 1,
    arrows: false,
    pagination: false,
    type: "fade",
    drag: false,
  };

  const thumbnailsRef = useRef<Splide>(null);
  const cardsRef = useRef<Splide>(null);

  useEffect(() => {
    if (
      thumbnailsRef.current &&
      cardsRef.current &&
      cardsRef.current.splide &&
      thumbnailsRef.current.splide
    ) {
      cardsRef.current.sync(thumbnailsRef.current.splide);
    }
  }, [thumbnailsRef, cardsRef]);

  const changeUrlPath = useCallback(
    (id: string) => {
      navigate(RouteFactory.pulseSlide(id), { replace: true });
      if (id === "finished") {
        sendTrackingData("pulse outro", "pulse/outro");
      } else {
        sendTrackingData("", "", id, sortedPulseData);
      }
    },
    [navigate, sortedPulseData, sendTrackingData]
  );

  const slideMovedChangeUrl = (index: number, id: string) => {
    if (index === totalLength - 1) {
      changeUrlPath("finished");
      return;
    }

    if (id === "") return;
    changeUrlPath(id);
  };

  const slideChanges = (index: number, id: string) => {
    setCurrentSlide(index);
    setCurrentSlideId(id);
  };

  if (sortedPulseData.length === 0) return <> </>;

  const handleNextSlide = () => {
    cardsRef.current?.go(currentSlide + 1);
    handleOnClickGaTracking("next");
  };

  const handlePrevSlides = () => {
    cardsRef.current?.go(currentSlide - 1);
    handleOnClickGaTracking("previous");
  };

  const handleOnClickGaTracking = (action: string) => {
    const article = memorisedArticles.find(
      (article) => article.id === currentSlideId
    );

    sendCustomEvent(action, article?.title || "(not set)");
  };

  return (
    <div
      className="relative flex h-svh w-screen flex-col"
      data-testid="pulse-mobile-carousel-component"
    >
      <div className="relative z-10 mt-2 flex h-[2px] w-full gap-1 px-2">
        {Array.from({ length: totalLength }).map((_, index) => {
          return (
            <div
              key={`pulse-mobile-carousel-pagination-${index}`}
              className={cn("h-[2px] bg-gray-650")}
              style={{
                width: `calc(100%/${memorisedArticles.length})`,
              }}
            >
              <div
                className={cn(
                  "h-[2px] w-0 bg-white transition-all duration-150",
                  {
                    "w-full": index <= currentSlide,
                  }
                )}
              />
            </div>
          );
        })}
      </div>

      <div className="absolute top-0 h-svh">
        <Splide
          options={optionsForImage}
          hasTrack={false}
          ref={thumbnailsRef}
          className="[&>div>ul>li]:!translate-x-0 [&>div>ul>li]:!transition-opacity [&>div>ul>li]:!duration-700"
        >
          <SplideTrack>
            {memorisedArticles.map((article) => {
              return (
                <Fragment key={article.id}>
                  <SplideSlide
                    className={cn(REMOVE_FOCUS_CSS)}
                    data-article-id={article.id}
                  >
                    <div
                      className="h-svh w-screen"
                      style={{
                        background: `center / cover no-repeat url(${article.media}?w=800&dpr=1&f=webp)`,
                      }}
                    >
                      <div className="absolute top-0 h-full w-full bg-gradient-to-b from-black to-80% opacity-70" />
                    </div>
                  </SplideSlide>
                </Fragment>
              );
            })}

            <SplideSlide>
              <div className=" h-svh w-screen bg-[#212121]">
                <div className="absolute top-0 h-full w-full bg-gradient-to-b from-black" />
              </div>
            </SplideSlide>
          </SplideTrack>
        </Splide>
      </div>

      <div className="relative top-0 flex-1 flex-grow items-center justify-center">
        <Splide
          className="h-full"
          options={options}
          hasTrack={false}
          onMoved={(splide, index) => {
            const activeSlideId =
              splide.Components.Elements.slides[index].getAttribute(
                "data-article-id"
              ) || "";
            slideMovedChangeUrl(index, activeSlideId);
          }}
          onMove={(splide, index, prev) => {
            splide.Components.Elements.slides[prev].classList.remove("prev");
            splide.Components.Elements.slides[prev].classList.remove("next");
            splide.Components.Elements.slides[prev].classList.remove("active");

            if (prev > index) {
              splide.Components.Elements.slides[prev].classList.add("next");
            } else if (prev < index) {
              splide.Components.Elements.slides[prev].classList.add("prev");
            }

            splide.Components.Elements.slides[index].classList.add("active");
            splide.Components.Elements.slides[index].classList.remove("prev");
            splide.Components.Elements.slides[index].classList.remove("next");

            const activeSlideId =
              splide.Components.Elements.slides[splide.index].getAttribute(
                "data-article-id"
              ) || "";
            slideChanges(splide.index, activeSlideId);
          }}
          ref={cardsRef}
        >
          <SplideTrack className="h-full">
            {memorisedArticles.map((article, index) => {
              return (
                <Fragment key={article.id}>
                  <SplideSlide
                    className={cn(
                      REMOVE_FOCUS_CSS,
                      "group h-full !transition-all duration-700"
                    )}
                    data-article-id={article.id}
                    data-slide-index={index}
                  >
                    <PulseIndividualMobileArticle article={article} />
                  </SplideSlide>
                </Fragment>
              );
            })}

            <SplideSlide className={cn(REMOVE_FOCUS_CSS, "group")}>
              <div className="flex h-full flex-col items-center justify-center p-4">
                <PulseEndingScreen backToBt={backToBt} />
              </div>
            </SplideSlide>
          </SplideTrack>
        </Splide>
      </div>

      <div className="relative flex  w-full items-center justify-between bg-black px-3 py-4">
        <Link
          reloadDocument
          to={`${backToBt}?ref=pulse`}
          className="inline-flex w-full items-center font-poppins text-sm font-semibold tracking-2% text-white"
          onClick={() => {
            handleOnClickGaTracking("back to bt");
          }}
        >
          <img
            src={chevronLeft}
            className="h-6 w-6"
            alt="left arrow"
            width={24}
            height={24}
          />
          Back to BT
        </Link>

        <div className="flex w-full justify-end">
          <button
            className={cn(
              REMOVE_FOCUS_CSS,
              "flex flex-none items-center gap-2 font-poppins text-sm font-semibold tracking-2% text-white transition-all duration-300",
              { "pointer-events-none opacity-50": currentSlide === 0 }
            )}
            onClick={handlePrevSlides}
          >
            <img
              src={chevronLeft}
              className="h-6 w-6"
              alt="right arrow"
              width={24}
              height={24}
            />
            Previous
          </button>

          <div className="mx-3 h-auto w-[1px] flex-none bg-gray-750"></div>
          <button
            onClick={handleNextSlide}
            className={cn(
              REMOVE_FOCUS_CSS,
              "flex flex-none items-center gap-2 font-poppins text-sm font-semibold tracking-2% text-white transition-all duration-300",
              {
                "pointer-events-none opacity-50":
                  currentSlide === totalLength - 1,
              }
            )}
          >
            Next
            <img
              src={chevronRight}
              className="h-6 w-6"
              alt="left arrow"
              width={24}
              height={24}
            />
          </button>
        </div>
      </div>
    </div>
  );
}
